// Page-facing forecast hook.
//
// Phase 3C: the Weibull failure forecast now runs SERVER-SIDE at
// POST /api/v2/analysis/forecast/weibull (Phase 3B). This hook is a thin fetch
// over that endpoint via TanStack Query, replacing the former web-worker /
// main-thread compute path. The public return contract is preserved verbatim so
// ForecastPage renders unchanged.
//
// Client-side population accounting (usePopulationAnalysis, fed by useFailures +
// useTurbineForPopulation) is RETAINED: the page needs populationSize /
// forecastSubsetSize / validationErrors / canCalculate available reactively
// BEFORE a forecast is run (sidebar counts, gating, subset-validation warnings).
// That is population counting + filter-subset validation for the UI, not
// forecast math — the heavy Monte-Carlo computation is what moved to the server.

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FailureFilters } from '@/types/filters';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { createLogger } from '@/utils/logger';
import {
  ForecastResult,
  ForecastConfig,
  SimulationMetadata,
  TurbineForForecast,
} from '@/types/forecast';
import { useFailures } from '@/hooks/useFailures';
import { FailureRecord } from '@/types/failure';
import { FORECAST_CALCULATION_CONSTANTS } from '@/constants/forecastCalculation';
import { usePopulationAnalysis } from '@/hooks/usePopulationAnalysis';
import { useTurbineForPopulation } from '@/hooks/useTurbines';
import { analysisApi } from '@/services/api/analysisApi';

interface UseForecastCalculationProps {
  sourceAnalysis: WeibullAnalysisResult | null;
  filters: FailureFilters;
  configuration: ForecastConfig;
}

interface ForecastCalculationResult {
  // Results
  forecastResults: ForecastResult[];
  populationSize: number;
  forecastSubsetSize: number;
  simulationMetadata: SimulationMetadata | null;

  // Loading states
  isCalculating: boolean;
  turbinesLoading: boolean;
  // `undefined` while a calculation is in flight: server compute is opaque, so we
  // suppress the determinate progress bar entirely (WaveLoading renders no bar for
  // `undefined`) rather than pin it at a misleading stuck-at-0%. 100 when idle.
  calculationProgress: number | undefined;
  progressText: string;

  // Validation
  canCalculate: boolean;
  validationErrors: string[];

  // Actions
  calculateForecast: () => Promise<void>;
  clearResults: () => void;
  setForecastResults: (results: ForecastResult[], metadata?: SimulationMetadata) => void;

  // Debug info
  _turbineData?: FailureRecord[];
  _turbinesForForecast?: TurbineForForecast[];
}

const logger = createLogger('ForecastCalculation');

export const useForecastCalculation = ({
  sourceAnalysis,
  filters,
  configuration,
}: UseForecastCalculationProps): ForecastCalculationResult => {
  // Manually-loaded (workspace-restored) results take precedence over the query
  // result. When set, the query result is ignored until the next calculate.
  const [manualResults, setManualResults] = useState<ForecastResult[] | null>(null);
  const [manualMetadata, setManualMetadata] = useState<SimulationMetadata | null>(null);

  // Trigger gate + per-run seed. The forecast is an explicit user action
  // ("Calculate Forecast"), so the query stays disabled until calculateForecast()
  // sets a seed. `runSeed === null` means "not yet triggered". A FRESH seed is
  // generated on every explicit calculate; because the seed is part of the query
  // key, each press is a distinct cache entry and re-runs the queryFn — this
  // reproduces the worker's per-click fresh Monte-Carlo run (a stable seed would
  // otherwise serve the cached result within staleTime and never recompute).
  const [runSeed, setRunSeed] = useState<number | null>(null);
  const triggered = runSeed !== null;

  // ---- Client-side population accounting (retained, used for gating/display) ----
  const baseFilterOptions = {
    size: FORECAST_CALCULATION_CONSTANTS.TURBINE_FETCH_SIZE,
    page: 1,
    excludedCaseIds: [] as string[],
  };

  const failureFilters = { ...filters, ...baseFilterOptions };
  const { data: turbineData, isLoading: turbinesLoading } = useFailures(failureFilters);

  // The population turbine list is fetched against the SOURCE analysis filters
  // (matching the worker hook), falling back to the current filters.
  const filterForTurbines = sourceAnalysis?.metadata
    ? sourceAnalysis.metadata.filtersUsed
    : filters;
  const { data: populationTurbineData } = useTurbineForPopulation({
    ...filterForTurbines,
    ...baseFilterOptions,
  });

  // `populationErrors` is the single source of truth: it gates both `canCalculate`
  // and the query `enabled`, and is surfaced verbatim as `validationErrors`. (A
  // previous useState/useEffect re-emission via useValidationErrors lagged by one
  // render, so the displayed errors could disagree with the gating predicate.)
  const {
    populationSize,
    forecastSubsetSize,
    turbinesForForecast,
    validationErrors: populationErrors,
  } = usePopulationAnalysis({ turbineData, populationTurbineData, sourceAnalysis, filters });

  // ---- Server-side forecast computation (the moved work) ----
  // sourceAnalysis can carry a `metadata` field (WeibullAnalysisResultWithMetadata);
  // narrowing the fit fields used in the cache key keeps the key stable across
  // unrelated metadata mutations while still re-fetching when the fit changes.
  const fitKey = sourceAnalysis
    ? {
        shape: sourceAnalysis.parameters?.shape,
        scale: sourceAnalysis.parameters?.scale,
        // The MLE result drives the parametric-bootstrap uncertainty, so it must
        // vary the cache. Reference identity is sufficient (a new analysis load
        // produces a new object); JSON-stringifying a large object every render
        // would be wasteful.
        mle: sourceAnalysis.mleResult,
      }
    : null;

  const {
    data: queryResponse,
    isFetching,
    error,
  } = useQuery({
    // Cache key includes the full filters, the full configuration, the source
    // fit, AND the per-run seed — changing ANY of them re-fetches. The seed makes
    // every explicit "Calculate" a distinct run (mirrors the worker's per-click
    // fresh Monte-Carlo seed) instead of serving a stale cached result.
    queryKey: ['weibull-forecast', filters, configuration, fitKey, runSeed],
    queryFn: () => analysisApi.getWeibullForecast(sourceAnalysis!, filters, configuration, runSeed!),
    enabled: triggered && !!sourceAnalysis && populationSize > 0 && populationErrors.length === 0,
    staleTime: 60000,
    cacheTime: 600000,
    refetchOnWindowFocus: false,
  });

  // Gating: identical predicate to the legacy hooks. `isCalculating` (below)
  // folds in the in-flight query state.
  const isCalculating = triggered && isFetching;

  const canCalculate = useMemo(() => {
    return !!(
      sourceAnalysis &&
      populationSize > 0 &&
      populationErrors.length === 0 &&
      !turbinesLoading &&
      !isCalculating
    );
  }, [sourceAnalysis, populationSize, populationErrors, turbinesLoading, isCalculating]);

  // Effective results/metadata: a manual (workspace-restored) load wins; once a
  // calculation is triggered, the live query result takes over.
  const forecastResults: ForecastResult[] =
    manualResults ?? (triggered ? queryResponse?.results ?? [] : []);
  const simulationMetadata: SimulationMetadata | null =
    manualMetadata ?? (triggered ? queryResponse?.metadata ?? null : null);

  const calculateForecast = useCallback(async () => {
    if (!sourceAnalysis || populationSize === 0 || populationErrors.length > 0) {
      logger.warn('Cannot calculate forecast: validation failed');
      return;
    }
    // Drop any workspace-restored results so the live query result surfaces, and
    // arm the query with a FRESH seed so every press recomputes (the seed is part
    // of the query key). The fetch is driven by useQuery once enabled flips true;
    // returning a resolved promise preserves the `() => Promise<void>` contract
    // the page awaits — the loading overlay is driven by `isCalculating`.
    setManualResults(null);
    setManualMetadata(null);
    setRunSeed(Date.now());
  }, [sourceAnalysis, populationSize, populationErrors]);

  const clearResults = useCallback(() => {
    setManualResults(null);
    setManualMetadata(null);
    setRunSeed(null);
  }, []);

  // Load pre-calculated results (e.g. restoring a saved forecast workspace).
  const setForecastResults = useCallback(
    (results: ForecastResult[], metadata?: SimulationMetadata) => {
      setManualResults(results);
      if (metadata) {
        setManualMetadata(metadata);
      }
      // Loading a saved forecast should not leave the live query "armed".
      setRunSeed(null);
    },
    [],
  );

  if (error) {
    logger.error('Forecast calculation failed:', error);
  }

  return {
    // Results
    forecastResults,
    populationSize,
    forecastSubsetSize,
    simulationMetadata,

    // Loading states
    isCalculating,
    turbinesLoading,
    // Worker per-stage progress is gone (server compute is opaque). While a run is
    // in flight we report `undefined` so WaveLoading suppresses the determinate bar
    // (a hardcoded 0 would render as a permanently stuck-at-0% bar). The overlay's
    // text comes from progressText below.
    calculationProgress: isCalculating ? undefined : 100,
    progressText: isCalculating ? 'Calculating forecast...' : '',

    // Validation: `populationErrors` is returned directly so the displayed errors
    // never lag the gating predicate by a render.
    canCalculate,
    validationErrors: populationErrors,

    // Actions
    calculateForecast,
    clearResults,
    setForecastResults,

    // Debug info
    _turbineData: turbineData?.failures,
    _turbinesForForecast: turbinesForForecast.slice(0, 5),
  };
};

// The forecast now always runs server-side; web workers are no longer used.
export const isUsingWebWorkers = false;
